"use strict";

/**
 * Clase `Region` para gestionar la selección en los controles de región.
 */
export class Region {
    /**
     * Crea una instancia de la clase Region.
     * @param {Object} httpError - Objeto para manejar errores HTTP.
     * @param {Object} tool - Objeto para métodos auxiliares
     */
    constructor(httpError, tool) {
        this.httpError = httpError;
        this.tool = tool;
    }

    /**
     * Maneja el cambio de región en el selector.
     * @param {HTMLElement} container - Contenedor padre del selector.
     * @param {HTMLSelectElement} regionSelect - Selector de regiones.
     * @param {string} type - Tipo de región ('country' o 'child').
     * @param {string} className - Clase CSS para nuevos selectores hijos.
     * @param {string} idParent - ID para el campo padre.
     * @param {string} idSon - ID para el campo hijo.
     */
    changeRegion(container, regionSelect, type, className, idParent, idSon) {
        regionSelect.addEventListener("change", async (event) => {
            try {
                /** Elimina selectores hijos existentes si el tipo es country o child */
                if (type === "country") {
                    document
                        .querySelectorAll(`.${className}`)
                        ?.forEach((el) => el.remove());
                } else if (
                    type === "child" &&
                    container.nextElementSibling?.classList.contains(className)
                ) {
                    container.nextElementSibling.remove();
                }

                /** Muestra el spinner mientras se realiza la petición */
                const spinner = document.querySelector("#spinner");
                spinner?.classList.remove("hidden");

                const regionValue = regionSelect.value;
                const url = `/regions/count/${regionValue}`;

                /** Consulta si la región tiene hijos */
                const { data } = await axios.put(url, { regionValue });
                spinner?.classList.add("hidden");

                if (data === 0) {
                    /** Si no hay hijos, asigna ID y Name de hijo */
                    regionSelect.id = idSon;
                    regionSelect.name = idSon;
                } else {
                    /** Si hay hijos, asigna ID y Name de padre y muestra subregiones */
                    regionSelect.id = idParent;
                    regionSelect.name = idParent;
                    await this.showRegions(
                        container,
                        regionValue,
                        className,
                        idParent,
                        idSon
                    );
                }
            } catch (error) {
                this.httpError?.handleHttpError(error);
            }
        });
    }

    /**
     * Inicializa la selección de país para el primer campo de región.
     */
    chooseCountry() {
        const selectors = ["#div_parent", "#region_id"];

        if (!this.tool.areElementsPresent(selectors)) return;

        const div = document.querySelector("#div_parent");
        const region = document.querySelector("#region_id");

        this.tool.initSelectr(region);

        /** Cambio de región */
        this.changeRegion(
            div,
            region,
            "country",
            "div_children",
            "parent_id",
            "region_id"
        );
    }

    /**
     * Inicializa la selección de país para la expedición del documento.
     */
    chooseCountryTwo() {
        const selectors = ["#div_expedition_parent", "#region_expedition_id"];

        if (!this.tool.areElementsPresent(selectors)) return;

        const div = document.querySelector("#div_expedition_parent");
        const region = document.querySelector("#region_expedition_id");

        this.tool.initSelectr(region);

        /** Cambio de región */
        this.changeRegion(
            div,
            region,
            "country",
            "div_expedition_children",
            "parent_expedition_id",
            "region_expedition_id"
        );
    }

    /**
     * Inicializa la selección de país para el formulario de estudios.
     */
    chooseCountryThree() {
        const selectors = [
            "#academic_title_id",
            "#div_academic_title_id",
            "#div_external_academic_title",
            "#div_external_institution",
            "#div_institution",
            "#external_academic_title",
            "#external_institution",
            "#institution_id",
            "#label_external_academic_title",
            "#region_id",
        ];

        if (!this.tool.areElementsPresent(selectors)) return;

        const elements = {
            academicTitle: document.querySelector("#academic_title_id"),
            divAcademicTitle: document.querySelector("#div_academic_title_id"),
            divExternalAcademicTitle: document.querySelector(
                "#div_external_academic_title"
            ),
            divExternalInstitution: document.querySelector(
                "#div_external_institution"
            ),
            divInstitution: document.querySelector("#div_institution"),
            externalAcademicTitle: document.querySelector(
                "#external_academic_title"
            ),
            externalInstitution: document.querySelector(
                "#external_institution"
            ),
            institution: document.querySelector("#institution_id"),
            labelExternalAcademicTitle: document.querySelector(
                "#label_external_academic_title"
            ),
            region: document.querySelector("#region_id"),
        };

        this.tool.initSelectr(elements.institution);
        this.tool.initSelectr(elements.region);

        elements.region.addEventListener("change", (event) => {
            const selectedText = event.target.selectedOptions[0]?.text || "";

            const isColombia = selectedText === "-COLOMBIA";

            /** Según el país, muestra u oculta campos */
            this.toggleFields(isColombia, elements);
        });
    }

    /**
     * Muestra las regiones hijas en un nuevo selector.
     * @param {HTMLElement} parentDiv - Contenedor padre donde agregar el nuevo selector.
     * @param {string} region - ID de la región padre.
     * @param {string} className - Clase CSS para nuevos selectores.
     * @param {string} idParent - ID para el campo padre.
     * @param {string} idSon - ID para el campo hijo.
     */
    async showRegions(parentDiv, region, className, idParent, idSon) {
        try {
            const url = `/regions/get/${region}`;
            const { data } = await axios.put(url, { region });

            /** Crea la estructura del nuevo selector */
            const divRegion = document.createElement("div");
            divRegion.className = `items-center w-full gap-3 mb-1 md:grid md:grid-cols-3 ${className}`;

            const label = document.createElement("label");
            label.className = "mb-0 font-bold text-slate-600";

            const divSelect = document.createElement("div");
            divSelect.className = "md:col-span-2";

            const select = document.createElement("select");
            select.className =
                "w-full px-3 py-2 leading-tight bg-white border border-slate-300 rounded appearance-none focus:shadow-outline focus:bg-slate-200/75 placeholder:text-gray-600";
            select.title = "Elija la región";
            select.required = true;
            select.id = idSon;
            select.name = idSon;

            const optionDefault = document.createElement("option");
            optionDefault.text = "--Seleccione--";
            optionDefault.value = "";
            optionDefault.disabled = true;
            optionDefault.selected = true;

            select.appendChild(optionDefault);

            /** Agrega las opciones de las regiones hijas */
            data.forEach(({ name, id }) => {
                const option = document.createElement("option");
                option.text = name;
                option.value = id;
                select.appendChild(option);
            });

            divSelect.appendChild(select);
            divRegion.append(label, divSelect);

            parentDiv.parentNode.insertBefore(divRegion, parentDiv.nextSibling);

            this.tool.initSelectr(select);

            this.changeRegion(
                divRegion,
                select,
                "child",
                className,
                idParent,
                idSon
            );
        } catch (error) {
            this.httpError?.handleHttpError(error);
        }
    }

    /**
     * Muestra u oculta los campos según el país seleccionado.
     * @param {boolean} isColombia - Indica si el país seleccionado es Colombia.
     * @param {object} elements - Elementos del formulario.
     */
    toggleFields(isColombia, elements) {
        const show = (el) => {
            el.classList.remove("hidden");
            el.classList.add("md:grid");
        };

        const hide = (el) => {
            el.classList.add("hidden");
            el.classList.remove("md:grid");
        };

        if (isColombia) {
            /** Muestra selectores internos y oculta campos externos */
            show(elements.divInstitution);
            show(elements.divAcademicTitle);
            elements.institution.required = true;
            elements.academicTitle.required = true;
            hide(elements.divExternalInstitution);
            hide(elements.divExternalAcademicTitle);
            elements.externalInstitution.required = false;
            elements.externalAcademicTitle.required = false;
            elements.externalInstitution.value = "";
            elements.externalAcademicTitle.value = "";
        } else {
            /** Oculta selectores internos y muestra campos externos */
            hide(elements.divInstitution);
            hide(elements.divAcademicTitle);
            elements.institution.required = false;
            elements.academicTitle.required = false;
            show(elements.divExternalInstitution);
            show(elements.divExternalAcademicTitle);
            elements.externalInstitution.required = true;
            elements.externalAcademicTitle.required = true;
            elements.labelExternalAcademicTitle?.classList.remove("invisible");
        }
    }
}
